import { CircleNotchIcon, IconProps } from "@phosphor-icons/react"
import classNames from "classnames"
import React, { ComponentType, JSX, useMemo } from "react"

import { ButtonSize, ButtonType } from "shared/types/Button"

type ButtonProps = {
    buttonType: ButtonType
    text: string
    iconLeft?: ComponentType<IconProps>
    iconRight?: ComponentType<IconProps>
    enabled: boolean
    onClick: () => void
    loading?: boolean
    className?: string
    size?: ButtonSize
    props?: any
}

export default function Button({
    buttonType,
    text,
    iconLeft: IconLeftComponent,
    iconRight: IconRightComponent,
    enabled = true,
    onClick,
    loading,
    className,
    size = "large",
    ...props
}: ButtonProps): JSX.Element {
    const buttonClass = classNames(
        [
            "select-none",
            "self-stretch",
            "h-fit",
            "flex",
            "justify-center",
            "gap-2",

            "shadow-sm",
            "font-semibold",
            "rounded-md",
            "border",
            "border-solid",
            "border-border-primary",
            "whitespace-nowrap",
            "items-center",
        ],
        {
            "px-2 py-1 text-sm min-w-10 min-h-8": size === "tiny",
            "px-2 py-1 text-sm min-w-24 min-h-8": size === "small",
            "px-4 py-2 text-base min-w-32 min-h-10": size === "medium",
            "px-4 py-3 text-lg min-w-40 min-h-12": size === "large",
        },
        {
            "hover:underline cursor-pointer": enabled,
        },
        {
            "opacity-50 cursor-not-allowed": !enabled,
        },
        {
            "bg-primary text-text-on-primary fill-text-on-primary hover:bg-primary-hover active:bg-primary-active":
                buttonType === ButtonType.Primary,
        },
        {
            "bg-white text-text-on-secondary fill-text-on-secondary hover:bg-secondary-hover active:bg-secondary-active":
                buttonType === ButtonType.Secondary,
        },
        {
            "bg-tertiary text-text-on-tertiary fill-text-on-tertiary hover:bg-tertiary-hover active:bg-tertiary-active":
                buttonType === ButtonType.Tertiary,
        },
    )

    const iconSize = useMemo(() => {
        if (size === "tiny") return 12
        if (size === "small") return 14
        return 18
    }, [size])

    const handleClick = (mouseEvent: React.MouseEvent<HTMLButtonElement>) => {
        mouseEvent.preventDefault()
        if (enabled) {
            onClick()
        }
    }

    return (
        <button
            type="button"
            disabled={!enabled}
            className={classNames(buttonClass, className)}
            onClick={handleClick}
            {...props}
        >
            {IconLeftComponent && !loading && (
                <IconLeftComponent weight="bold" size={iconSize} />
            )}
            {loading && (
                <CircleNotchIcon
                    weight="bold"
                    className="animate-spin"
                    size={iconSize}
                />
            )}
            {text}
            {IconRightComponent && !loading && (
                <IconRightComponent weight="bold" size={iconSize} />
            )}
        </button>
    )
}

export { Button, ButtonType }
